import React, { useState, useEffect } from 'react';
import {
    Users,
    RefreshCw,
    Calendar,
    Search,
    AlertCircle,
    Phone,
    CheckCircle,
    FileText,
    UserPlus,
    UserMinus,
    Ban,
    Eye,
    Edit,
    Bot,
    X,
    ChevronDown
} from 'lucide-react';
import { EnrollmentApi } from '@/features/enrollment';

// Funnel colors for application status
const APPLICATION_FUNNEL_COLORS = [
    '#3b82f6', // blue - New lead
    '#8b5cf6', // purple - Need Addtl Data
    '#06b6d4', // cyan - Authenticated
    '#84cc16', // lime - Pending Consent
    '#f59e0b', // amber - Submitted - Addtl Docs
    '#10b981', // emerald - Enrolled
    '#6366f1', // indigo - Service Ended
    '#ef4444', // red - Left BoB
];

const STEP_NAMES = [
    'New Lead',
    'Need Addtl Data',
    'Authenticated',
    'Pending Consent',
    'Submitted - Addtl Docs',
    'Enrolled',
    'Service Ended',
    'Left BoB'
];

interface ApplicationFunnelStepData {
    step: number;
    name: string;
    count: number;
    percentage: number;
    color: string;
}

interface ApplicationFunnelStats {
    funnel: ApplicationFunnelStepData[];
    totalApplications: number;
}

interface ApplicationClient {
    id: string;
    clientName: string;
    email: string;
    status: string;
    updatedAt: string;
    campaignId: string;
    data: any;
    isExcommunicado?: boolean;
    currentStep?: number;
}

interface ClientAction {
    id: string;
    label: string;
    icon: React.ComponentType<{ className?: string }>;
    color: string;
    onClick: () => void;
}

// Funnel Visualization Component (Trapezoid)
const FunnelVisualization: React.FC<{
    data: ApplicationFunnelStepData[];
    selectedStep: number | null;
    onStepClick: (step: number) => void;
}> = ({ data, selectedStep, onStepClick }) => {
    const totalWidth = 600;
    const stepHeight = 50;
    const gap = 4;
    const minWidth = 120;
    const maxWidth = totalWidth;

    return (
        <div className="flex flex-col items-center py-6">
            <svg
                viewBox={`0 0 ${totalWidth} ${data.length * (stepHeight + gap)}`}
                className="w-full max-w-2xl"
                preserveAspectRatio="xMidYMid meet"
            >
                {data.map((step, index) => {
                    // Calculate trapezoid widths (progression style)
                    const topWidthRatio = 1 - (index / data.length) * 0.7;
                    const bottomWidthRatio = 1 - ((index + 1) / data.length) * 0.7;
                    const topWidth = minWidth + (maxWidth - minWidth) * topWidthRatio;
                    const bottomWidth = minWidth + (maxWidth - minWidth) * bottomWidthRatio;

                    const centerX = totalWidth / 2;
                    const y = index * (stepHeight + gap);

                    const topLeft = centerX - topWidth / 2;
                    const topRight = centerX + topWidth / 2;
                    const bottomLeft = centerX - bottomWidth / 2;
                    const bottomRight = centerX + bottomWidth / 2;

                    const path = `M ${topLeft} ${y} L ${topRight} ${y} L ${bottomRight} ${y + stepHeight} L ${bottomLeft} ${y + stepHeight} Z`;

                    const isSelected = selectedStep === step.step;

                    return (
                        <g
                            key={step.step}
                            onClick={() => onStepClick(step.step)}
                            className="cursor-pointer transition-opacity hover:opacity-90"
                        >
                            <path
                                d={path}
                                fill={APPLICATION_FUNNEL_COLORS[index] || '#cbd5e1'}
                                stroke={isSelected ? '#1e293b' : 'transparent'}
                                strokeWidth={isSelected ? 3 : 0}
                                className="transition-all duration-200"
                            />
                            <text
                                x={centerX}
                                y={y + stepHeight / 2}
                                textAnchor="middle"
                                dominantBaseline="middle"
                                fill="white"
                                fontSize="14"
                                fontWeight="600"
                                className="pointer-events-none"
                            >
                                {step.name}
                            </text>
                            <text
                                x={centerX}
                                y={y + stepHeight / 2 + 16}
                                textAnchor="middle"
                                dominantBaseline="middle"
                                fill="white"
                                fontSize="12"
                                opacity="0.9"
                                className="pointer-events-none"
                            >
                                {step.count.toLocaleString()} ({step.percentage}%)
                            </text>
                        </g>
                    );
                })}
            </svg>
        </div>
    );
};

// Status Change Modal Component
const StatusChangeModal: React.FC<{
    isOpen: boolean;
    onClose: () => void;
    client: ApplicationClient | null;
    onStatusChange: (targetStep: number, reason: string) => Promise<void>;
}> = ({ isOpen, onClose, client, onStatusChange }) => {
    const [targetStep, setTargetStep] = useState<number>(1);
    const [reason, setReason] = useState('');
    const [loading, setLoading] = useState(false);

    if (!isOpen || !client) return null;

    const handleSubmit = async () => {
        setLoading(true);
        try {
            await onStatusChange(targetStep, reason);
            onClose();
        } catch (error) {
            console.error('Error changing status:', error);
        } finally {
            setLoading(false);
        }
    };

    return (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50">
            <div className="bg-white rounded-xl shadow-xl max-w-md w-full mx-4 p-6">
                <div className="flex justify-between items-center mb-4">
                    <h3 className="text-lg font-bold text-gray-900">Change Status</h3>
                    <button onClick={onClose} className="text-gray-400 hover:text-gray-600">
                        <X className="w-5 h-5" />
                    </button>
                </div>

                <div className="mb-4">
                    <p className="text-sm text-gray-600 mb-2">
                        Client: <span className="font-medium">{client.clientName}</span>
                    </p>
                </div>

                <div className="mb-4">
                    <label className="block text-sm font-medium text-gray-700 mb-1">Target Step</label>
                    <div className="relative">
                        <select
                            value={targetStep}
                            onChange={(e) => setTargetStep(Number(e.target.value))}
                            className="w-full px-3 py-2 border border-gray-300 rounded-lg appearance-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                        >
                            {STEP_NAMES.map((name, idx) => (
                                <option key={idx + 1} value={idx + 1}>
                                    Step {idx + 1}: {name}
                                </option>
                            ))}
                        </select>
                        <ChevronDown className="absolute right-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400 pointer-events-none" />
                    </div>
                </div>

                <div className="mb-6">
                    <label className="block text-sm font-medium text-gray-700 mb-1">Reason (optional)</label>
                    <textarea
                        value={reason}
                        onChange={(e) => setReason(e.target.value)}
                        placeholder="Enter reason for status change..."
                        className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                        rows={3}
                    />
                </div>

                <div className="flex gap-3">
                    <button
                        onClick={onClose}
                        className="flex-1 px-4 py-2 border border-gray-300 rounded-lg text-gray-700 hover:bg-gray-50 transition-colors"
                    >
                        Cancel
                    </button>
                    <button
                        onClick={handleSubmit}
                        disabled={loading}
                        className="flex-1 px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors disabled:opacity-50"
                    >
                        {loading ? 'Updating...' : 'Update Status'}
                    </button>
                </div>
            </div>
        </div>
    );
};

// Excommunicado Modal Component
const ExcommunicadoModal: React.FC<{
    isOpen: boolean;
    onClose: () => void;
    client: ApplicationClient | null;
    isRemoving: boolean;
    onConfirm: (reason: string) => Promise<void>;
}> = ({ isOpen, onClose, client, isRemoving, onConfirm }) => {
    const [reason, setReason] = useState('');
    const [loading, setLoading] = useState(false);

    if (!isOpen || !client) return null;

    const handleSubmit = async () => {
        if (!isRemoving && !reason.trim()) {
            alert('Please provide a reason for blacklisting this client.');
            return;
        }
        setLoading(true);
        try {
            await onConfirm(reason);
            onClose();
        } catch (error) {
            console.error('Error updating excommunicado status:', error);
        } finally {
            setLoading(false);
        }
    };

    return (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50">
            <div className="bg-white rounded-xl shadow-xl max-w-md w-full mx-4 p-6">
                <div className="flex justify-between items-center mb-4">
                    <h3 className="text-lg font-bold text-gray-900">
                        {isRemoving ? 'Remove Blacklist' : 'Blacklist Client'}
                    </h3>
                    <button onClick={onClose} className="text-gray-400 hover:text-gray-600">
                        <X className="w-5 h-5" />
                    </button>
                </div>

                <div className="mb-4">
                    <p className="text-sm text-gray-600">
                        Client: <span className="font-medium">{client.clientName}</span>
                    </p>
                </div>

                {isRemoving ? (
                    <div className="mb-6 p-4 bg-yellow-50 border border-yellow-200 rounded-lg">
                        <p className="text-sm text-yellow-800">
                            Are you sure you want to remove the blacklist (Excommunicado) flag from this client?
                            They will be able to be enrolled again.
                        </p>
                    </div>
                ) : (
                    <>
                        <div className="mb-4 p-4 bg-red-50 border border-red-200 rounded-lg">
                            <p className="text-sm text-red-800">
                                <strong>Warning:</strong> Setting the Excommunicado flag will permanently blacklist
                                this client from future enrollments. This is typically used when a client makes
                                false complaints against agents or the company.
                            </p>
                        </div>

                        <div className="mb-6">
                            <label className="block text-sm font-medium text-gray-700 mb-1">
                                Reason <span className="text-red-500">*</span>
                            </label>
                            <textarea
                                value={reason}
                                onChange={(e) => setReason(e.target.value)}
                                placeholder="Enter reason for blacklisting..."
                                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-red-500 focus:border-red-500"
                                rows={3}
                                required
                            />
                        </div>
                    </>
                )}

                <div className="flex gap-3">
                    <button
                        onClick={onClose}
                        className="flex-1 px-4 py-2 border border-gray-300 rounded-lg text-gray-700 hover:bg-gray-50 transition-colors"
                    >
                        Cancel
                    </button>
                    <button
                        onClick={handleSubmit}
                        disabled={loading || (!isRemoving && !reason.trim())}
                        className={`flex-1 px-4 py-2 rounded-lg text-white transition-colors disabled:opacity-50 ${isRemoving
                            ? 'bg-yellow-600 hover:bg-yellow-700'
                            : 'bg-red-600 hover:bg-red-700'
                            }`}
                    >
                        {loading ? 'Processing...' : isRemoving ? 'Remove Blacklist' : 'Blacklist Client'}
                    </button>
                </div>
            </div>
        </div>
    );
};

// Client Card Component with Actions
const ClientCard: React.FC<{
    client: ApplicationClient;
    selectedStep: number;
    onStatusChange: () => void;
    onExcommunicado: (isRemoving: boolean) => void;
    onAction: (action: string, client: ApplicationClient) => void;
}> = ({ client, selectedStep, onStatusChange, onExcommunicado, onAction }) => {
    const getActionsForStep = (step: number): ClientAction[] => {
        const actions: ClientAction[] = [];

        switch (step) {
            case 1: // New Lead
                actions.push({
                    id: 'process_ai',
                    label: 'Process',
                    icon: Bot,
                    color: 'blue',
                    onClick: () => onAction('process_ai', client)
                });
                break;

            case 2: // Need Addtl Data
                actions.push({
                    id: 'update_data',
                    label: 'Update',
                    icon: Edit,
                    color: 'blue',
                    onClick: () => onAction('update_data', client)
                });
                actions.push({
                    id: 'retry_ai',
                    label: 'Retry',
                    icon: RefreshCw,
                    color: 'green',
                    onClick: () => onAction('retry_ai', client)
                });
                break;

            case 3: // Authenticated
                actions.push({
                    id: 'capture_consent',
                    label: 'Consent',
                    icon: CheckCircle,
                    color: 'green',
                    onClick: () => onAction('capture_consent', client)
                });
                break;

            case 4: // Pending Consent
                actions.push({
                    id: 'call_client',
                    label: 'Call',
                    icon: Phone,
                    color: 'blue',
                    onClick: () => onAction('call_client', client)
                });
                actions.push({
                    id: 'mark_consent',
                    label: 'Consent Redone',
                    icon: CheckCircle,
                    color: 'green',
                    onClick: () => onAction('mark_consent', client)
                });
                break;

            case 5: // Submitted - Need Docs
                actions.push({
                    id: 'request_docs',
                    label: 'Request',
                    icon: FileText,
                    color: 'blue',
                    onClick: () => onAction('request_docs', client)
                });
                actions.push({
                    id: 'verify_docs',
                    label: 'Verify',
                    icon: CheckCircle,
                    color: 'green',
                    onClick: () => onAction('verify_docs', client)
                });
                break;

            case 6: // Enrolled
                actions.push({
                    id: 'view_details',
                    label: 'View',
                    icon: Eye,
                    color: 'blue',
                    onClick: () => onAction('view_details', client)
                });
                break;

            case 7: // Service Ended
                actions.push({
                    id: 'claim_reenroll',
                    label: 'Re-enroll',
                    icon: UserPlus,
                    color: 'green',
                    onClick: () => onAction('claim_reenroll', client)
                });
                actions.push({
                    id: 'mark_left_bob',
                    label: 'Left BoB',
                    icon: UserMinus,
                    color: 'red',
                    onClick: () => onAction('mark_left_bob', client)
                });
                break;

            case 8: // Left BoB
                if (client.isExcommunicado) {
                    actions.push({
                        id: 'remove_excommunicado',
                        label: 'Unblacklist',
                        icon: Ban,
                        color: 'yellow',
                        onClick: () => onExcommunicado(true)
                    });
                } else {
                    actions.push({
                        id: 'set_excommunicado',
                        label: 'Blacklist',
                        icon: Ban,
                        color: 'red',
                        onClick: () => onExcommunicado(false)
                    });
                }
                break;
        }

        return actions;
    };

    const actions = getActionsForStep(selectedStep);

    const getButtonClass = (color: string) => {
        switch (color) {
            case 'blue':
                return 'bg-blue-100 text-blue-700 hover:bg-blue-200';
            case 'green':
                return 'bg-green-100 text-green-700 hover:bg-green-200';
            case 'red':
                return 'bg-red-100 text-red-700 hover:bg-red-200';
            case 'yellow':
                return 'bg-yellow-100 text-yellow-700 hover:bg-yellow-200';
            default:
                return 'bg-gray-100 text-gray-700 hover:bg-gray-200';
        }
    };

    return (
        <div className="p-3 bg-gray-50 rounded-lg border border-gray-100 hover:border-blue-200 transition-all">
            <div className="flex justify-between items-start mb-2">
                <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2">
                        <p className="font-medium text-gray-900 truncate">{client.clientName}</p>
                        {client.isExcommunicado && (
                            <span className="px-1.5 py-0.5 bg-red-100 text-red-700 text-[10px] font-bold rounded uppercase">
                                Blacklisted
                            </span>
                        )}
                    </div>
                    <p className="text-xs text-gray-500 truncate">{client.email}</p>
                </div>
                <div className="flex items-center gap-1">
                    <span className="px-2 py-0.5 bg-white text-[10px] font-bold uppercase rounded border border-gray-200">
                        {client.status}
                    </span>
                    <button
                        onClick={onStatusChange}
                        className="p-1 text-gray-400 hover:text-blue-600 hover:bg-blue-50 rounded transition-colors"
                        title="Change Status"
                    >
                        <Edit className="w-3.5 h-3.5" />
                    </button>
                </div>
            </div>

            <div className="flex items-center justify-between">
                <div className="flex items-center gap-1 text-[10px] text-gray-400">
                    <Calendar className="w-3 h-3" />
                    {new Date(client.updatedAt).toLocaleDateString()}
                </div>

                <div className="flex gap-1">
                    {actions.map((action) => {
                        const Icon = action.icon;
                        return (
                            <button
                                key={action.id}
                                onClick={action.onClick}
                                className={`px-2 py-1 text-[10px] font-medium rounded flex items-center gap-1 transition-colors ${getButtonClass(action.color)}`}
                                title={action.label}
                            >
                                <Icon className="w-3 h-3" />
                                {action.label}
                            </button>
                        );
                    })}
                </div>
            </div>
        </div>
    );
};

interface ApplicationStatusFunnelProps {
    campaignId?: string;
    onStepSelect?: (step: number | null) => void;
    compact?: boolean;
}

// Main Component
export function ApplicationStatusFunnel({ campaignId, onStepSelect, compact = false }: ApplicationStatusFunnelProps) {
    const [stats, setStats] = useState<ApplicationFunnelStats | null>(null);
    const [selectedStep, setSelectedStep] = useState<number | null>(null);
    const [clients, setClients] = useState<ApplicationClient[]>([]);
    const [pagination, setPagination] = useState<{ total: number; page: number; limit: number; totalPages: number } | null>(null);
    const [page, setPage] = useState(1);
    const [loading, setLoading] = useState(true);
    const [clientsLoading, setClientsLoading] = useState(false);
    const [error, setError] = useState<string | null>(null);

    // Modal states
    const [statusModalOpen, setStatusModalOpen] = useState(false);
    const [excommunicadoModalOpen, setExcommunicadoModalOpen] = useState(false);
    const [selectedClient, setSelectedClient] = useState<ApplicationClient | null>(null);
    const [isRemovingExcommunicado, setIsRemovingExcommunicado] = useState(false);

    // Search state
    const [searchTerm, setSearchTerm] = useState('');

    const loadStats = async () => {
        try {
            setLoading(true);
            const data = await EnrollmentApi.getFunnelStats(campaignId);
            setStats(data);
            setError(null);
        } catch (err) {
            console.error('Error loading application funnel stats:', err);
            setError('Failed to load funnel statistics');
        } finally {
            setLoading(false);
        }
    };

    const loadClients = async (step: number, targetPage: number = 1) => {
        try {
            setClientsLoading(true);
            const res = await EnrollmentApi.getFunnelClients(step, campaignId, targetPage, 10);
            console.log(`[Funnel] Loaded ${res?.clients?.length || 0} clients for step ${step}`);
            setClients(res?.clients || []);
            setPagination(res?.pagination || null);
        } catch (err) {
            console.error('Error loading funnel clients:', err);
        } finally {
            setClientsLoading(false);
        }
    };

    useEffect(() => {
        loadStats();
    }, [campaignId]);

    useEffect(() => {
        if (selectedStep) {
            loadClients(selectedStep, page);
        } else {
            setClients([]);
            setPagination(null);
        }
    }, [selectedStep, campaignId, page]);

    const handleStepClick = (step: number) => {
        console.log(`[Funnel] Step ${step} clicked`);
        const newStep = selectedStep === step ? null : step;
        setSelectedStep(newStep);
        setPage(1); // Reset page on step change
        // Notify parent component if callback provided
        if (onStepSelect) {
            onStepSelect(newStep);
        }
    };

    const handleStatusChange = async (targetStep: number, reason: string) => {
        if (!selectedClient) return;

        await EnrollmentApi.updateFunnelStatus(selectedClient.id, targetStep, { reason });
        loadStats();
        if (selectedStep) {
            loadClients(selectedStep);
        }
    };

    const handleExcommunicado = async (reason: string) => {
        if (!selectedClient) return;

        if (isRemovingExcommunicado) {
            await EnrollmentApi.removeExcommunicado(selectedClient.id);
        } else {
            await EnrollmentApi.setExcommunicado(selectedClient.id, reason);
        }
        loadStats();
        if (selectedStep) {
            loadClients(selectedStep);
        }
    };

    const handleAction = async (action: string, client: ApplicationClient) => {
        switch (action) {
            case 'claim_reenroll':
                try {
                    await EnrollmentApi.assignReEnrollment(client.id);
                    // Move to enrolled after claiming
                    await EnrollmentApi.updateFunnelStatus(client.id, 6, { reason: 'Re-enrolled by agent' });
                    loadStats();
                    if (selectedStep) loadClients(selectedStep);
                } catch (err: any) {
                    alert(err.response?.data?.error || 'Failed to claim for re-enrollment');
                }
                break;

            case 'mark_left_bob':
                await EnrollmentApi.updateFunnelStatus(client.id, 8, { reason: 'Marked as left book of business' });
                loadStats();
                if (selectedStep) loadClients(selectedStep);
                break;

            case 'mark_consent':
                await EnrollmentApi.updateFunnelStatus(client.id, 5, { reason: 'Consent captured' });
                loadStats();
                if (selectedStep) loadClients(selectedStep);
                break;

            case 'process_ai':
            case 'retry_ai':
                try {
                    await EnrollmentApi.processAIRobot(client.id);
                    loadStats();
                    if (selectedStep) loadClients(selectedStep);
                } catch (err: any) {
                    alert(err.response?.data?.error || 'Failed to process AI robot');
                }
                break;

            case 'verify_docs':
                await EnrollmentApi.updateFunnelStatus(client.id, 6, { reason: 'Documents verified' });
                loadStats();
                if (selectedStep) loadClients(selectedStep);
                break;

            // Other actions would trigger navigation or external processes
            default:
                console.log(`Action ${action} triggered for client ${client.id}`);
        }
    };

    const filteredClients = (clients || []).filter(client =>
        client.clientName.toLowerCase().includes(searchTerm.toLowerCase()) ||
        client.email.toLowerCase().includes(searchTerm.toLowerCase())
    );

    if (loading && !stats) {
        return (
            <div className="flex items-center justify-center p-12">
                <RefreshCw className="w-8 h-8 animate-spin text-blue-500" />
            </div>
        );
    }

    if (error) {
        return (
            <div className="p-6 bg-red-50 text-red-600 rounded-lg flex items-center gap-2">
                <AlertCircle className="w-5 h-5" />
                {error}
            </div>
        );
    }

    return (
        <div className="bg-white rounded-xl shadow-sm border border-gray-100 overflow-hidden">
            <div className="p-6 border-b border-gray-50 flex justify-between items-center">
                <div>
                    <h2 className="text-xl font-bold text-gray-800">Application Status Funnel</h2>
                    <p className="text-sm text-gray-500">Track application progress through the 8-step pipeline</p>
                </div>
                <div className="flex items-center gap-3">
                    <button
                        onClick={loadStats}
                        className="p-2 text-gray-400 hover:text-blue-500 transition-colors"
                        title="Refresh Stats"
                    >
                        <RefreshCw className={`w-5 h-5 ${loading ? 'animate-spin' : ''}`} />
                    </button>
                </div>
            </div>

            <div className={`${compact ? 'p-6' : 'grid grid-cols-1 lg:grid-cols-2 gap-8 p-6'}`}>
                {/* Left: Funnel Visualization */}
                <div className={`flex flex-col items-center ${compact ? 'max-w-xl mx-auto' : ''}`}>
                    <FunnelVisualization
                        data={stats?.funnel || []}
                        selectedStep={selectedStep}
                        onStepClick={handleStepClick}
                    />
                    <div className="mt-4 text-center">
                        <span className="text-3xl font-bold text-gray-900">{stats?.totalApplications}</span>
                        <p className="text-sm font-medium text-gray-500 uppercase tracking-wider">Total Applications</p>
                    </div>
                </div>

                {/* Right: Client List - Hidden in compact mode */}
                {!compact && (
                    <div className="flex flex-col h-full border-t lg:border-t-0 lg:border-l border-gray-100 pt-8 lg:pt-0 lg:pl-8">
                        <div className="flex items-center justify-between mb-4">
                            <div className="flex items-center gap-2">
                                <Users className="w-5 h-5 text-gray-400" />
                                <h3 className="font-semibold text-gray-700">
                                    {selectedStep
                                        ? `Step ${selectedStep}: ${stats?.funnel.find(s => s.step === selectedStep)?.name}`
                                        : 'Select a step to view clients'}
                                </h3>
                            </div>
                            {selectedStep && clients.length > 0 && (
                                <span className="text-sm text-gray-500">{filteredClients.length} clients</span>
                            )}
                        </div>

                        {/* Search Bar */}
                        {selectedStep && clients.length > 0 && (
                            <div className="mb-4 relative">
                                <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
                                <input
                                    type="text"
                                    value={searchTerm}
                                    onChange={(e) => setSearchTerm(e.target.value)}
                                    placeholder="Search by name or email..."
                                    className="w-full pl-9 pr-4 py-2 border border-gray-200 rounded-lg text-sm focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                                />
                            </div>
                        )}

                        <div className="flex-1 overflow-y-auto min-h-[400px]">
                            {clientsLoading ? (
                                <div className="flex justify-center p-8">
                                    <RefreshCw className="w-6 h-6 animate-spin text-gray-300" />
                                </div>
                            ) : filteredClients.length > 0 ? (
                                <div className="space-y-3">
                                    {filteredClients.map(client => (
                                        <ClientCard
                                            key={client.id}
                                            client={client}
                                            selectedStep={selectedStep!}
                                            onStatusChange={() => {
                                                setSelectedClient(client);
                                                setStatusModalOpen(true);
                                            }}
                                            onExcommunicado={(isRemoving) => {
                                                setSelectedClient(client);
                                                setIsRemovingExcommunicado(isRemoving);
                                                setExcommunicadoModalOpen(true);
                                            }}
                                            onAction={handleAction}
                                        />
                                    ))}
                                </div>
                            ) : selectedStep ? (
                                <div className="flex flex-col items-center justify-center p-12 text-gray-400 italic">
                                    <Users className="w-12 h-12 mb-2 opacity-20" />
                                    {searchTerm ? 'No clients match your search' : 'No clients found at this step'}
                                </div>
                            ) : (
                                <div className="flex flex-col items-center justify-center p-12 text-gray-400 italic">
                                    <Search className="w-12 h-12 mb-2 opacity-20" />
                                    Select a funnel stage to filter the list
                                </div>
                            )}
                        </div>

                        {/* Pagination UI */}
                        {pagination && (
                            <div className="flex items-center justify-between px-4 py-3 border-t border-gray-100 bg-gray-50/50">
                                <div className="flex items-center gap-2">
                                    <p className="text-sm text-gray-700">
                                        Showing {clients.length} of {pagination.total} clients
                                        {pagination.totalPages > 1 && (
                                            <span className="ml-2 text-gray-400">
                                                (Page {pagination.page} of {pagination.totalPages})
                                            </span>
                                        )}
                                    </p>
                                </div>
                                {pagination.totalPages > 1 && (
                                    <div className="flex gap-2">
                                        <button
                                            onClick={() => setPage(p => Math.max(1, p - 1))}
                                            disabled={pagination.page === 1}
                                            className="px-3 py-1 text-xs font-medium bg-white border border-gray-200 rounded-lg hover:bg-gray-50 disabled:opacity-50 transition-colors"
                                        >
                                            Previous
                                        </button>
                                        <button
                                            onClick={() => setPage(p => Math.min(pagination.totalPages, p + 1))}
                                            disabled={pagination.page === pagination.totalPages}
                                            className="px-3 py-1 text-xs font-medium bg-white border border-gray-200 rounded-lg hover:bg-gray-50 disabled:opacity-50 transition-colors"
                                        >
                                            Next
                                        </button>
                                    </div>
                                )}
                            </div>
                        )}
                    </div>
                )}
            </div>

            {/* Modals */}
            <StatusChangeModal
                isOpen={statusModalOpen}
                onClose={() => {
                    setStatusModalOpen(false);
                    setSelectedClient(null);
                }}
                client={selectedClient}
                onStatusChange={handleStatusChange}
            />

            <ExcommunicadoModal
                isOpen={excommunicadoModalOpen}
                onClose={() => {
                    setExcommunicadoModalOpen(false);
                    setSelectedClient(null);
                }}
                client={selectedClient}
                isRemoving={isRemovingExcommunicado}
                onConfirm={handleExcommunicado}
            />
        </div >
    );
}
